import { requestHatchAPI } from './request'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const env = (key: string): string => import.meta.env[key] ?? ''

export interface HatchSettings {
  enabled: boolean
}

const CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes

interface CacheEntry {
  value: HatchSettings
  expiresAt: number
}

const cache: Record<string, CacheEntry> = {}

const fetchSettings = async (locationName: string, apiType: 'admin' | 'client'): Promise<HatchSettings> => {
  if (!env('VITE_HATCH_BASE_URL')) return { enabled: false }

  const cacheKey = `${apiType}:${locationName}`
  const now = Date.now()
  const cached = cache[cacheKey]
  if (cached && cached.expiresAt > now) return cached.value

  try {
    const settings = await requestHatchAPI<HatchSettings>({
      url: `/${apiType}/${locationName}/settings`,
      method: 'GET',
    })
    cache[cacheKey] = { value: settings, expiresAt: now + CACHE_TTL_MS }
    return settings
  } catch {
    // Fail open — route to Goose, no Hatch blocks
    const fallback: HatchSettings = { enabled: false }
    cache[cacheKey] = { value: fallback, expiresAt: now + CACHE_TTL_MS }
    return fallback
  }
}

/** Whether Hatch is enabled for a location on the admin surface. */
export const isHatchEnabledAdmin = async (locationName: string): Promise<boolean> => {
  const settings = await fetchSettings(locationName, 'admin')
  return settings.enabled
}

/** Whether Hatch is enabled for a location on the customer surface. */
export const isHatchEnabledClient = async (locationName: string): Promise<boolean> => {
  const settings = await fetchSettings(locationName, 'client')
  return settings.enabled
}

/** React-query compatible settings fetcher for admin. */
export const getHatchSettingsAdmin = (locationName: string) => fetchSettings(locationName, 'admin')

/** React-query compatible settings fetcher for client. */
export const getHatchSettingsClient = (locationName: string) => fetchSettings(locationName, 'client')
